import _get from 'lodash/get'
import { get as getConfig } from 'config'
import { fromJS, List, Map } from 'immutable'
import { containsUtm } from 'services/inbound-tracking'
import { SET_USER_DATA_LANGUAGE_PRIMARY, SET_USER_DATA_LANGUAGE } from 'services/user/actions'
import { SET_INBOUND_TRACKING_DATA_INITIALIZED } from 'services/inbound-tracking/actions'
import { SET_EVENT_PAGE_VIEWED, setEventDefault } from 'services/event-tracking/actions'
import { SET_APP_STORE_INITIALIZED } from 'services/app/actions'
import { SET_CHECKOUT_STEP, SET_CHECKOUT_ORDER_DATA } from 'services/checkout/actions'
import { SET_PLANS_SELECTION } from 'services/plans/actions'
import { SET_AUTH_DATA } from 'services/auth/actions'
import { createAbsolute } from 'services/url'
import { SET_PAYTRACK_DATA_LAST_TRANSACTION } from 'services/paytrack/actions'
import { SET_ONBOARDING_COMPLETED } from 'services/onboarding/actions'
import _isEqual from 'lodash/isEqual'
import _isEmpty from 'lodash/isEmpty'
import { OPTIMIZELY_FEATURE_TEST_ENROLLMENT_GA4_EVENT } from 'services/event-tracking'
import { SET_USER_ACCOUNT_DATA_BILLING_SUBSCRIPTIONS_WITH_DETAILS } from 'services/user-account/actions'
import {
  SET_OPTIMIZELY_FEATURE_MOUNTED,
  SET_OPTIMIZELY_FEATURE_USER_ATTRIBUTE,
  setOptimizelyFeatureDecision,
  setOptimizelyFeatureUser,
  updateOptimizelyFeatureUser,
  SET_OPTIMIZELY_FEATURE_EVENT,
} from './actions'
import { getOptimizelyFeatureClient, getOptimizelyFeatureUser, setOptimizelyFeatureEvent } from '.'

const config = getConfig()
const optimizelyEnabled = !!_get(config, ['features', 'optimizelyFeatureTesting'])

/**
 * Initialize the Optimizely feature user in the store.
 * This function retrieves the user data from the state and dispatches an action
 * to set the Optimizely feature user.
 * @param {object} store - redux store
 * @returns {void}
 */
function initializeUser (store) {
  const state = store.getState()
  const user = getOptimizelyFeatureUser(state)
  store.dispatch(setOptimizelyFeatureUser(user))
}

/**
 * Set the user authentication status in the Optimizely feature user attributes.
 * This function retrieves the authentication status and user ID from the action payload
 * and dispatches an action to update the Optimizely feature user attributes.
 * @param {object} store - redux store
 * @param {object} action - redux action containing user data
 * @returns {void}
 */
function setUserAuth (store) {
  const state = store.getState()
  const user = getOptimizelyFeatureUser(state)
  store.dispatch(updateOptimizelyFeatureUser(user))
}

/**
 * Set the plans selection in the Optimizely feature user attributes.
 * This function retrieves the user ID and SKU from the action payload
 * and dispatches an action to set the Optimizely feature event for plan selection.
 * @param {object} store - redux store
 * @param {object} action - redux action containing plan selection data
 * @returns {void}
 */
function setPlansSelection (store, action) {
  const state = store.getState()
  const { optimizelyFeature = Map() } = state
  const userId = optimizelyFeature.getIn(['user', 'id'])
  const data = _get(action, ['payload', 'data'])
  const sku = fromJS(data).get('sku')

  if (userId && sku) {
    setOptimizelyFeatureEvent('plan_selection', userId, { SKU: sku })
  }
}

/**
 * Set the user language attribute in the Optimizely feature user attributes.
 * This function retrieves the language from the action payload and dispatches
 * an action to update the Optimizely feature user with the language.
 * @param {object} store - redux store
 * @param {object} action - redux action containing language data
 * @returns {void}
 */
function setUserLanguageAttribute (store, action) {
  const language = _get(action, ['payload', 'value'], null)
  if (List.isList(language)) {
    store.dispatch(updateOptimizelyFeatureUser({ user_language: language.first() }))
  }
}

/**
 * Add inbound tracking attributes to the Optimizely feature user.
 * This function checks if the inbound tracking data contains UTM parameters
 * and updates the Optimizely feature user attributes accordingly.
 * @param {object} store - redux store
 * @param {object} action - redux action containing inbound tracking data
 * @returns {void}
 */
function setInboundTrackingAttributes (store, action) {
  const data = _get(action, ['payload', 'data'], Map())
  const dataContainsUtm = containsUtm(data)
  store.dispatch(updateOptimizelyFeatureUser({
    user_tracked: dataContainsUtm ? 'true' : 'false',
  }))
}

/**
 * Set the updated variation for the Optimizely feature flags.
 * This function checks if the Optimizely client is mounted and if the user data has changed.
 * If so, it updates the user and retrieves the latest decisions for each flag.
 * @param {object} store - redux store
 * @returns {void}
 */
function setUpdatedVariation (store) {
  const state = store.getState()
  const optimizelyFeature = state.optimizelyFeature || Map()
  let activeFlags = List()

  const isMounted = optimizelyFeature.get('mounted', false)
  const flags = optimizelyFeature.get('flags', Map())
  const user = optimizelyFeature.get('user', Map()).toJS()
  const optimizely = getOptimizelyFeatureClient()

  const hasValidOptimizelyClient = !!optimizely
  const userUpdated = hasValidOptimizelyClient && !_isEqual(user, optimizely.user)

  const shouldRun = hasValidOptimizelyClient && isMounted && !_isEmpty(user)
  if (!shouldRun) return

  flags.keySeq().forEach((flagKey) => {
    const storedDecision = flags.get(flagKey) || Map()
    const isDecisionEmpty = storedDecision.isEmpty()

    // make or update decision
    if (userUpdated || isDecisionEmpty) {
      optimizely.setUser(user)
      const decision = optimizely.decide(flagKey)
      const isFlagEnabled = !!decision.enabled
      const ga4EventSent = flags.getIn([flagKey, 'ga4EventSent'], false)

      // send ga4 event
      if (isFlagEnabled && !ga4EventSent) {
        setTimeout(() => {
          store.dispatch(setEventDefault(OPTIMIZELY_FEATURE_TEST_ENROLLMENT_GA4_EVENT))
        }, 1000)
      }

      // Add to active tests
      if (isFlagEnabled) {
        activeFlags = activeFlags.push(flagKey)
      }

      // set decision
      store.dispatch(setOptimizelyFeatureDecision(decision, true))
    }
  })

  // Store on user state if we have any active flags, store them as a string separated by commas
  if (activeFlags.size > 0) {
    store.dispatch(updateOptimizelyFeatureUser({ user_active_flags: activeFlags.join(',') }))
  }
}

/**
 * Get the page view attributes for the Optimizely feature user.
 * This function retrieves the current page URL, referrer, and path from the state
 * and constructs the attributes to be sent with the page view event.
 * @param {object} state - The current application state.
 * @returns {object} - The page view attributes including URL, referrer, and path.
 */
function getPageViewAttributes (state) {
  const { resolver = Map(), optimizelyFeature = Map() } = state
  const { origin } = config

  const location = resolver.get('location') || {}
  const path = resolver.get('path') || '/'
  const oldPath = optimizelyFeature.getIn(['user', 'attributes', 'page_path']) || '/'

  const url = (global && global.location) ? global.location.href : `${createAbsolute(path)}${location.search || ''}`
  let referrer = (global && global.document) ? global.document.referrer : ''
  if (!referrer || referrer.includes(origin)) {
    referrer = createAbsolute(oldPath)
  }

  return {
    url,
    referrer,
    path,
  }
}

/**
 * Set the page viewed event for the Optimizely feature user.
 * This function retrieves the user ID and page attributes from the state,
 * and sends a page view event to the Optimizely feature service.
 * It also updates the user attributes with the current page information.
 * @param {object} store - redux store
 * @returns {void}
 */
function setPageViewed (store) {
  const state = store.getState()
  const { optimizelyFeature = Map() } = state
  const userId = optimizelyFeature.getIn(['user', 'id'])
  const pageAttributes = getPageViewAttributes(state)

  // Sent Optimizely Feature event
  if (userId) {
    setOptimizelyFeatureEvent('page_view', userId, {
      URL: pageAttributes.url,
      path: pageAttributes.path,
    })
  }

  // Update user page
  store.dispatch(updateOptimizelyFeatureUser({
    page_url: pageAttributes.url,
    page_path: pageAttributes.path,
    page_referrer: pageAttributes.referrer,
  }))
}

/**
 * Set the checkout step for the Optimizely feature user.
 * This function retrieves the current checkout step from the action payload
 * and sends a checkout step event to the Optimizely feature service.
 * @param {object} store - redux store
 * @param {object} action - redux action containing the checkout step
 * @returns {void}
 */
function setCheckoutStep (store, action) {
  const state = store.getState()
  const { optimizelyFeature = Map() } = state

  const step = _get(action, ['payload'])
  const userId = optimizelyFeature.getIn(['user', 'id'])

  if (userId && step) {
    setOptimizelyFeatureEvent('checkout_step', userId, { step })
  }
}

/**
 * Send the Optimizely feature event.
 * This function retrieves the user ID and event name from the action payload
 * and sends a feature event to the Optimizely feature service.
 * @param {object} store - redux store
 * @param {object} action - redux action containing the event name and tags
 * @returns {void}
 */
function sendOptimizelyFeatureEvent (store, action) {
  const { optimizelyFeature = Map() } = store.getState()
  const userId = optimizelyFeature.getIn(['user', 'id'])
  const eventName = _get(action, ['payload', 'eventName'])
  const tags = _get(action, ['payload', 'tags'], {})

  if (userId && eventName) {
    setOptimizelyFeatureEvent(eventName, userId, tags)
  }
}

/**
 * Set the tenure days for the Optimizely feature user.
 * This function retrieves the last transaction from the state
 * and calculates the tenure days between the last transaction and the current date.
 * @param {object} store - redux store
 * @returns {void}
 */
function setTenureDays (store) {
  const { paytrack = Map() } = store.getState()
  const createdAt = paytrack.getIn(['lastTransaction', 'createdAt'])

  // compare createdAt with current date
  const currentDate = new Date()
  const createdAtDate = new Date(createdAt)
  const tenure = currentDate.getTime() - createdAtDate.getTime()

  // transform tenure to days and round to nearest integer
  const tenureDays = Math.round(tenure / (1000 * 60 * 60 * 24))

  // set the tenure
  store.dispatch(updateOptimizelyFeatureUser({ user_tenure_days: tenureDays }))
}

/**
 * Set the signup status, this function returns true
 * if the user has recently signed up
 * @param {object} store - redux store
 * @returns {void}
 */
function setSignupStatus (store) {
  const { checkout = Map() } = store.getState()
  const orderSuccess = !!checkout.getIn(['orderData', 'success'])
  const checkoutInitiated = !!checkout.get('checkoutInitiated')

  if (orderSuccess && checkoutInitiated) {
    store.dispatch(updateOptimizelyFeatureUser({ user_new_signup: true }))
  }
}

/**
 * Set the onboarding choices for the Optimizely feature user.
 * This function retrieves the onboarding choices from the state
 * and sends a onboarding choices event to the Optimizely feature service.
 * @param {object} store - redux store
 * @returns {void}
 */
function setOnboardingChoices (store) {
  const { onboarding = Map() } = store.getState()
  const onboardingChoices = onboarding.get('onboardingChoices') || List()

  store.dispatch(updateOptimizelyFeatureUser({
    user_onboarding_choices: onboardingChoices.join(', '),
    user_onboarding_size: onboardingChoices.length || 0,
  }))
}

/**
 * Set the last plan SKU for the Optimizely feature user.
 * This function retrieves the last plan from the state
 * and sends a last plan SKU event to the Optimizely feature service.
 * @param {object} store - redux store
 * @returns {void}
 */
function setUserLastPlan (store) {
  const { userAccount = Map() } = store.getState()
  const lastPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'planHistory', 0]) || Map()
  const lastPlanSku = lastPlan.get('planSku') || ''

  store.dispatch(updateOptimizelyFeatureUser({ user_last_plan_sku: lastPlanSku }))
}

/**
 * Redux middleware
 * @param {import('redux').Store} store A redux store
 * @returns {import('redux').Middleware} Redux middleware
 */
export default function middleware (store) {
  return (next) => (action) => {
    const type = _get(action, 'type')
    const result = next(action)

    if (!optimizelyEnabled) {
      return result
    }

    switch (type) {
      case SET_USER_DATA_LANGUAGE:
      case SET_USER_DATA_LANGUAGE_PRIMARY:
        setUserLanguageAttribute(store, action)
        break
      case SET_AUTH_DATA:
        setUserAuth(store)
        initializeUser(store)
        setUpdatedVariation(store)
        break
      case SET_PLANS_SELECTION:
        setPlansSelection(store, action)
        break
      case SET_INBOUND_TRACKING_DATA_INITIALIZED:
        setInboundTrackingAttributes(store, action)
        initializeUser(store)
        setUpdatedVariation(store)
        break
      case SET_OPTIMIZELY_FEATURE_USER_ATTRIBUTE:
      case SET_OPTIMIZELY_FEATURE_MOUNTED:
        setUpdatedVariation(store)
        break
      case SET_APP_STORE_INITIALIZED:
        initializeUser(store)
        setUpdatedVariation(store)
        break
      case SET_EVENT_PAGE_VIEWED:
        setPageViewed(store, action)
        break
      case SET_CHECKOUT_STEP:
        setCheckoutStep(store, action)
        break
      case SET_OPTIMIZELY_FEATURE_EVENT:
        sendOptimizelyFeatureEvent(store, action)
        break
      case SET_PAYTRACK_DATA_LAST_TRANSACTION:
        setTenureDays(store)
        break
      case SET_CHECKOUT_ORDER_DATA:
        setSignupStatus(store, action)
        break
      case SET_ONBOARDING_COMPLETED:
        setOnboardingChoices(store)
        break
      case SET_USER_ACCOUNT_DATA_BILLING_SUBSCRIPTIONS_WITH_DETAILS:
        setUserLastPlan(store)
        break
      default:
        break
    }

    return result
  }
}
